import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Plus, X, MapPin, Clock, Loader2, Trash2, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const input = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

export default function Calendario() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const [showTasks, setShowTasks] = useState(true);
  const [selected, setSelected] = useState(iso(new Date()));
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const load = useCallback(() => {
    const start = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const end = `${y}-${String(m + 1).padStart(2, "0")}-${String(new Date(y, m + 1, 0).getDate()).padStart(2, "0")}`;
    setLoading(true);
    api.get(`/calendar/feed?start=${start}&end=${end}&include_tasks=${showTasks}`)
      .then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [y, m, showTasks]);
  useEffect(() => { load(); }, [load]);

  const byDay = {};
  for (const it of items) { const d = (it.date || "").slice(0, 10); if (!d) continue; (byDay[d] ||= []).push(it); }

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(iso(new Date(y, m, d)));

  const daySel = byDay[selected] || [];
  const del = async (id) => { try { await api.delete(`/calendar/events/${id}`); toast.success("Evento eliminado"); load(); } catch { toast.error("Error"); } };

  return (
    <OSLayout title="Calendario">
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2">OMNIA · Agenda</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Calendario</h2>
            <p className="mt-2 text-navy/50">Tus eventos y tareas, todo en un solo lugar.</p>
          </motion.div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-navy/60 cursor-pointer" data-testid="cal-toggle-tasks">
              <input type="checkbox" checked={showTasks} onChange={(e) => setShowTasks(e.target.checked)} className="w-4 h-4 accent-violet" />
              <CheckSquare className="w-4 h-4" /> Ver tareas
            </label>
            <button data-testid="cal-new-event" onClick={() => setModal(true)}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
              <Plus className="w-4 h-4" /> Nuevo evento
            </button>
          </div>
        </div>

        {/* Month nav */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => setCursor(new Date(y, m - 1, 1))} className="p-2 rounded-lg border border-navy/10 hover:border-violet transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="cal-next" onClick={() => setCursor(new Date(y, m + 1, 1))} className="p-2 rounded-lg border border-navy/10 hover:border-violet transition-colors"><ChevronRight className="w-4 h-4" /></button>
            <h3 className="text-lg font-medium ml-2" data-testid="cal-month-label">{MES[m]} {y}</h3>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-navy/30" />}
          </div>
          <button onClick={() => { const t = new Date(); setCursor(new Date(t.getFullYear(), t.getMonth(), 1)); setSelected(iso(t)); }}
            className="text-sm text-navy/50 hover:text-violet transition-colors">Hoy</button>
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          {/* Grid */}
          <div className="bg-white border border-navy/10 rounded-lg p-3" data-testid="cal-grid">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DOW.map((d) => <div key={d} className="overline text-navy/40 text-center text-[0.6rem] py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => d === null ? <div key={`e${i}`} /> : (
                <button key={d} data-testid={`cal-day-${d}`} onClick={() => setSelected(d)}
                  className={`min-h-[84px] text-left rounded-lg p-1.5 border transition-colors ${selected === d ? "border-violet bg-violet/5" : "border-transparent hover:border-navy/10"} ${d === iso(new Date()) ? "ring-1 ring-violet/40" : ""}`}>
                  <div className="text-xs text-navy/50 mb-1">{Number(d.slice(-2))}</div>
                  <div className="space-y-1">
                    {(byDay[d] || []).slice(0, 3).map((it) => (
                      <div key={it.id} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${it.kind === "event" ? "bg-violet/15 text-violet" : "bg-navy/10 text-navy/60"}`}>
                        {it.start_time ? `${it.start_time} ` : ""}{it.title}
                      </div>
                    ))}
                    {(byDay[d] || []).length > 3 && <div className="text-[10px] text-navy/40 px-1.5">+{(byDay[d] || []).length - 3} más</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Day detail */}
          <div className="bg-white border border-navy/10 rounded-lg p-5" data-testid="cal-day-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">{selected}</h3>
              <button onClick={() => setModal(true)} className="text-xs text-violet hover:underline inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Evento</button>
            </div>
            {daySel.length === 0 ? <div className="text-navy/40 text-sm py-8 text-center">Sin eventos ni tareas.</div> : (
              <div className="space-y-2">
                {daySel.sort((a, b) => (a.start_time || "99").localeCompare(b.start_time || "99")).map((it) => (
                  <div key={it.id} data-testid={`cal-item-${it.id}`} className={`rounded-lg border p-3 ${it.kind === "event" ? "border-violet/20 bg-violet/5" : "border-navy/10"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{it.title}</div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-navy/50">
                          {(it.start_time || it.end_time) && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{it.start_time || "—"}{it.end_time ? `–${it.end_time}` : ""}</span>}
                          {it.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{it.location}</span>}
                          {it.kind === "task" && <span className="px-1.5 py-0.5 rounded bg-navy/10">{it.source}</span>}
                        </div>
                        {it.notes && <div className="text-[11px] text-navy/50 mt-1">{it.notes}</div>}
                      </div>
                      {it.kind === "event"
                        ? <button data-testid={`cal-del-${it.id}`} onClick={() => del(it.id)} className="text-navy/30 hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                        : <button onClick={() => navigate(it.link)} className="text-[11px] text-violet hover:underline shrink-0">Abrir</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {modal && <EventModal date={selected} onClose={() => setModal(false)} onCreated={() => { setModal(false); load(); }} />}
    </OSLayout>
  );
}

function EventModal({ date, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", date, start_time: "", end_time: "", location: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/calendar/events", f); toast.success("Evento creado"); onCreated(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="cal-event-modal" className="w-full max-w-md bg-cream rounded-xl p-6">
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-medium">Nuevo evento</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        <form onSubmit={submit} className="space-y-3">
          <input required data-testid="cal-event-title" placeholder="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={input} />
          <input required type="date" data-testid="cal-event-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={input} />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-navy/50">Hora inicio<input type="time" data-testid="cal-event-start" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className={`${input} mt-1`} /></label>
            <label className="text-xs text-navy/50">Hora fin<input type="time" data-testid="cal-event-end" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className={`${input} mt-1`} /></label>
          </div>
          <input placeholder="Lugar" data-testid="cal-event-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={input} />
          <textarea placeholder="Notas" data-testid="cal-event-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className={input} />
          <button data-testid="cal-event-submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar evento"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
